/**
 * @sazeo/sdk — Workflows hooks
 *
 * TanStack Query wrappers for the Workflow Automation module.
 * Admin-only: tenant admins define triggers, conditions, actions.
 * All calls use the user JWT. No service_role.
 *
 * Usage:
 *   const { data: workflows } = useWorkflows(supabase, tenantId);
 *   const createMutation = useCreateWorkflow(supabase, tenantId);
 *   const executeMutation = useExecuteWorkflow(supabase, tenantId);
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Workflow {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  trigger_event: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type ConditionOperator =
  | 'equals'
  | 'greater_than'
  | 'less_than'
  | 'contains'
  | 'is_in'
  | 'not_equals';
export type ActionType =
  | 'notify_user'
  | 'notify_role'
  | 'create_task'
  | 'send_email'
  | 'update_field'
  | 'trigger_webhook';
type WorkflowTaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type RoleType = 'manager' | 'admin' | 'hr';
export type WorkflowRunStatus = 'running' | 'completed' | 'failed' | 'skipped';

export interface WorkflowCondition {
  id?: string;
  workflow_id?: string;
  field: string;
  operator: ConditionOperator;
  value: string;
  order_index?: number;
}

export interface WorkflowAction {
  id?: string;
  workflow_id?: string;
  action_type: ActionType;
  target_user_id?: string | null;
  target_role?: RoleType | null;
  task_title?: string | null;
  task_assignee_id?: string | null;
  task_priority?: WorkflowTaskPriority;
  email_template?: string | null;
  field_name?: string | null;
  field_value?: string | null;
  webhook_url?: string | null;
  order_index?: number;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  description: string | null;
  trigger_event: string;
  is_active: boolean;
  conditions: WorkflowCondition[];
  actions: WorkflowAction[];
}

export interface WorkflowRun {
  id: string;
  tenant_id: string;
  workflow_id: string;
  trigger_entity_id: string;
  trigger_entity_type: string;
  status: WorkflowRunStatus;
  conditions_met: boolean | null;
  actions_executed: number;
  error_message: string | null;
  error_context: unknown | null;
  started_at: string;
  completed_at: string | null;
  created_at: string;
}

export interface WorkflowExecutionStep {
  id: string;
  workflow_run_id: string;
  step_type: 'condition' | 'action';
  step_index: number;
  step_name: string;
  passed: boolean | null;
  result_details: unknown | null;
  created_at: string;
}

export interface WorkflowSummary extends Workflow {
  conditions_count: number;
  actions_count: number;
  run_count: number;
  successful_run_count: number;
  failed_run_count: number;
  success_rate: number | null;
  last_run_at: string | null;
}

export interface CreateWorkflowInput {
  name: string;
  description?: string;
  trigger_event: string;
  is_active?: boolean;
  conditions?: WorkflowCondition[];
  actions: WorkflowAction[];
}

export interface UpdateWorkflowInput {
  name?: string;
  description?: string;
  is_active?: boolean;
  conditions?: WorkflowCondition[];
  actions?: WorkflowAction[];
}

export interface TriggerWorkflowInput {
  trigger_entity_id: string;
  trigger_entity_type: string;
  trigger_event: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const workflowsKeys = {
  all: ['workflows'] as const,
  list: (tenantId: string) => [...workflowsKeys.all, 'list', tenantId] as const,
  summaries: (tenantId: string) => [...workflowsKeys.all, 'summaries', tenantId] as const,
  detail: (workflowId: string) => [...workflowsKeys.all, 'detail', workflowId] as const,
  runs: (tenantId: string, workflowId?: string) =>
    [...workflowsKeys.all, 'runs', tenantId, workflowId ?? 'all'] as const,
  executionSteps: (runId: string) => [...workflowsKeys.all, 'executionSteps', runId] as const,
} as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch all active and inactive workflows for the tenant.
 */
export function useWorkflows(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery<Workflow[]>({
    queryKey: workflowsKeys.list(tenantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('updated_at', { ascending: false });

      if (error) throw new Error(error.message);
      return data || [];
    },
    enabled: Boolean(tenantId),
  });
}

/**
 * Fetch workflow list plus aggregate counts for list/table screens.
 */
export function useWorkflowSummaries(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery<WorkflowSummary[]>({
    queryKey: workflowsKeys.summaries(tenantId),
    queryFn: async () => {
      const { data: workflows, error: workflowsError } = await supabase
        .from('workflows')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('updated_at', { ascending: false });

      if (workflowsError) throw new Error(workflowsError.message);

      const rows = (workflows || []) as Workflow[];
      const workflowIds = rows.map((workflow) => workflow.id);
      if (workflowIds.length === 0) return [];

      const [runsResult, conditionsResult, actionsResult] = await Promise.all([
        supabase
          .from('workflow_runs')
          .select('*')
          .eq('tenant_id', tenantId)
          .in('workflow_id', workflowIds),
        supabase.from('workflow_conditions').select('workflow_id').in('workflow_id', workflowIds),
        supabase.from('workflow_actions').select('workflow_id').in('workflow_id', workflowIds),
      ]);

      if (runsResult.error) throw new Error(runsResult.error.message);
      if (conditionsResult.error) throw new Error(conditionsResult.error.message);
      if (actionsResult.error) throw new Error(actionsResult.error.message);

      const counts = new Map<
        string,
        {
          actions: number;
          conditions: number;
          failed: number;
          lastRunAt: string | null;
          runs: number;
          successful: number;
        }
      >();

      const ensure = (workflowId: string) => {
        const existing = counts.get(workflowId);
        if (existing) return existing;
        const created = {
          actions: 0,
          conditions: 0,
          failed: 0,
          lastRunAt: null,
          runs: 0,
          successful: 0,
        };
        counts.set(workflowId, created);
        return created;
      };

      for (const condition of (conditionsResult.data || []) as Array<{ workflow_id: string }>) {
        ensure(condition.workflow_id).conditions += 1;
      }

      for (const action of (actionsResult.data || []) as Array<{ workflow_id: string }>) {
        ensure(action.workflow_id).actions += 1;
      }

      for (const run of (runsResult.data || []) as WorkflowRun[]) {
        const entry = ensure(run.workflow_id);
        entry.runs += 1;
        if (run.status === 'completed') entry.successful += 1;
        if (run.status === 'failed') entry.failed += 1;
        if (!entry.lastRunAt || run.created_at > entry.lastRunAt) {
          entry.lastRunAt = run.created_at;
        }
      }

      return rows.map((workflow) => {
        const entry = ensure(workflow.id);
        const completedOrFailed = entry.successful + entry.failed;
        return {
          ...workflow,
          conditions_count: entry.conditions,
          actions_count: entry.actions,
          run_count: entry.runs,
          successful_run_count: entry.successful,
          failed_run_count: entry.failed,
          success_rate:
            completedOrFailed > 0 ? Math.round((entry.successful / completedOrFailed) * 100) : null,
          last_run_at: entry.lastRunAt,
        };
      });
    },
    enabled: Boolean(tenantId),
  });
}

/**
 * Fetch a single workflow with its conditions and actions.
 */
export function useWorkflow(supabase: SupabaseClientLike, workflowId: string) {
  return useQuery<WorkflowDefinition | null>({
    queryKey: workflowsKeys.detail(workflowId),
    queryFn: async () => {
      const { data: workflow, error: wError } = await supabase
        .from('workflows')
        .select('*')
        .eq('id', workflowId)
        .single();

      if (wError) throw new Error(wError.message);
      if (!workflow) return null;

      const { data: conditions, error: cError } = await supabase
        .from('workflow_conditions')
        .select('*')
        .eq('workflow_id', workflowId)
        .order('order_index', { ascending: true });

      if (cError) throw new Error(cError.message);

      const { data: actions, error: aError } = await supabase
        .from('workflow_actions')
        .select('*')
        .eq('workflow_id', workflowId)
        .order('order_index', { ascending: true });

      if (aError) throw new Error(aError.message);

      return {
        ...workflow,
        conditions: conditions || [],
        actions: actions || [],
      };
    },
    enabled: Boolean(workflowId),
  });
}

/**
 * Create a new workflow with conditions and actions.
 */
export function useCreateWorkflow(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateWorkflowInput) => {
      // Create the workflow
      const { data: workflow, error: wError } = await supabase
        .from('workflows')
        .insert([
          {
            tenant_id: tenantId,
            name: input.name,
            description: input.description || null,
            trigger_event: input.trigger_event,
            is_active: input.is_active !== false,
          },
        ])
        .select()
        .single();

      if (wError) throw new Error(wError.message);

      // Insert conditions
      if (input.conditions && input.conditions.length > 0) {
        const conditionsToInsert = input.conditions.map((c, idx) => ({
          workflow_id: workflow.id,
          field: c.field,
          operator: c.operator,
          value: c.value,
          order_index: idx,
        }));

        const { error: cError } = await supabase
          .from('workflow_conditions')
          .insert(conditionsToInsert);

        if (cError) throw new Error(cError.message);
      }

      // Insert actions
      if (input.actions.length > 0) {
        const actionsToInsert = input.actions.map((a, idx) => ({
          workflow_id: workflow.id,
          action_type: a.action_type,
          target_user_id: a.target_user_id || null,
          target_role: a.target_role || null,
          task_title: a.task_title || null,
          task_assignee_id: a.task_assignee_id || null,
          task_priority: a.task_priority || 'medium',
          email_template: a.email_template || null,
          field_name: a.field_name || null,
          field_value: a.field_value || null,
          webhook_url: a.webhook_url || null,
          order_index: idx,
        }));

        const { error: aError } = await supabase.from('workflow_actions').insert(actionsToInsert);

        if (aError) throw new Error(aError.message);
      }

      return workflow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowsKeys.list(tenantId) });
    },
  });
}

/**
 * Update an existing workflow (name, description, is_active).
 * Conditions and actions must be updated via separate mutation.
 */
export function useUpdateWorkflow(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workflowId,
      input,
    }: { workflowId: string; input: UpdateWorkflowInput }) => {
      const { error } = await supabase
        .from('workflows')
        .update({
          ...(input.name && { name: input.name }),
          ...(input.description !== undefined && { description: input.description }),
          ...(input.is_active !== undefined && { is_active: input.is_active }),
          updated_at: new Date().toISOString(),
        })
        .eq('id', workflowId);

      if (error) throw new Error(error.message);
    },
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowsKeys.list(tenantId) });
      queryClient.invalidateQueries({ queryKey: workflowsKeys.detail(workflowId) });
    },
  });
}

/**
 * Delete a workflow (and cascade deletes conditions, actions, runs).
 */
export function useDeleteWorkflow(supabase: SupabaseClientLike, tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workflowId: string) => {
      // Defense-in-depth: verify ownership before delete so an RLS misconfiguration
      // can't allow cross-tenant deletes.
      const { data: existing } = await supabase
        .from('workflows')
        .select('id')
        .eq('id', workflowId)
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (!existing) throw new Error('Workflow not found');

      const { error } = await supabase
        .from('workflows')
        .delete()
        .eq('id', workflowId)
        .eq('tenant_id', tenantId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowsKeys.list(tenantId) });
    },
  });
}

/**
 * Fetch all workflow runs for a tenant (optionally filtered by workflow).
 */
export function useWorkflowRuns(
  supabase: SupabaseClientLike,
  tenantId: string,
  workflowId?: string,
) {
  return useQuery<WorkflowRun[]>({
    queryKey: workflowsKeys.runs(tenantId, workflowId),
    queryFn: async () => {
      let query = supabase
        .from('workflow_runs')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (workflowId) {
        query = query.eq('workflow_id', workflowId);
      }

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data || [];
    },
    enabled: Boolean(tenantId),
  });
}

/**
 * Fetch execution steps for a specific workflow run.
 */
export function useWorkflowExecutionSteps(supabase: SupabaseClientLike, runId: string) {
  return useQuery<WorkflowExecutionStep[]>({
    queryKey: workflowsKeys.executionSteps(runId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_execution_steps')
        .select('*')
        .eq('workflow_run_id', runId)
        .order('step_index', { ascending: true });

      if (error) throw new Error(error.message);
      return data || [];
    },
    enabled: Boolean(runId),
  });
}

/**
 * Manually trigger a workflow execution (for testing or manual dispatch).
 * Calls Edge Function /app/workflows/trigger.
 */
export function useExecuteWorkflow(supabase: SupabaseClientLike, _tenantId: string) {
  return useMutation({
    mutationFn: async (input: TriggerWorkflowInput) => {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const supabaseUrl =
        typeof process !== 'undefined' && process.env
          ? process.env.REACT_APP_SUPABASE_URL
          : undefined;

      if (!token) throw new Error('Not authenticated');
      if (!supabaseUrl) throw new Error('Supabase URL is not configured');

      const response = await fetch(`${supabaseUrl}/functions/v1/app/workflows/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { error?: { message?: string } };
        throw new Error(errorData.error?.message || 'Workflow execution failed');
      }

      return response.json();
    },
  });
}
